import { useEffect, useMemo, useState } from 'react';
import { AGE_RATING, AGE_RATING_ODKR_MAP } from '../../constants/constant';
import {
  AgeRatingMaps,
  ageRatingMapApiService,
} from '../service/api/ageRatingMapApiService';

/**
 * 대응 등급을 병기할 서비스와 화면에 쓸 짧은 이름.
 *
 * 저장되는 값은 어디까지나 US 코드 하나뿐이고, 여기 나열된 것들은 "이 값이 각
 * 서비스로 나갈 때 무엇이 되는가" 를 보여주기 위한 표시 전용이다.
 */
const LABELLED_SERVICES: { serviceName: string; label: string }[] = [
  { serviceName: 'lg-channel-vietnam', label: 'VN' },
];

// VN_T16 -> T16. 저장값은 접두사를 포함하지만 화면에는 규정상의 등급명만 쓴다.
const stripServicePrefix = (code: string): string => code.replace(/^[A-Z]{2}_/, '');

/**
 * 하나의 US 등급에 대해 병기할 문구를 만든다.
 *
 * 예: TV-14 -> "TV-14 (ODKR R14 · VN T16)"
 * 대응 등급이 없으면 기존과 같은 "TV-14 (R14)" 로 남는다.
 */
export const buildAgeRatingLabel = (
  usCode: string,
  maps: AgeRatingMaps,
  odkrMap: Record<string, string> = AGE_RATING_ODKR_MAP
): string => {
  const parts: string[] = [];

  const odkr = odkrMap[usCode];
  if (odkr) parts.push(`ODKR ${odkr}`);

  LABELLED_SERVICES.forEach(({ serviceName, label }) => {
    const mapped = maps[serviceName]?.[usCode];
    if (mapped) parts.push(`${label} ${stripServicePrefix(mapped)}`);
  });

  return parts.length ? `${usCode} (${parts.join(' · ')})` : usCode;
};

/** 변환표를 한 번 받아서 재사용한다. 못 받으면 빈 표로 두고 기존 라벨만 쓴다. */
export const useAgeRatingMaps = (): AgeRatingMaps => {
  const [maps, setMaps] = useState<AgeRatingMaps>({});

  useEffect(() => {
    let active = true;
    ageRatingMapApiService.fetchMaps().then(result => {
      if (active) setMaps(result);
    });
    return () => {
      active = false;
    };
  }, []);

  return maps;
};

/**
 * 등급 드롭다운 옵션.
 *
 * 변환표를 못 받아도 US 등급은 그대로 고를 수 있다 — 병기가 안 될 뿐이다.
 * 영화는 MPAA 영화 등급을 쓰므로 코드 목록과 ODKR 표를 따로 넘긴다.
 */
export const useAgeRatingOptions = (
  codes: string[] = AGE_RATING,
  odkrMap: Record<string, string> = AGE_RATING_ODKR_MAP
): { value: string; label: string }[] => {
  const maps = useAgeRatingMaps();

  return useMemo(
    () =>
      codes.map(usCode => ({
        value: usCode,
        label: buildAgeRatingLabel(usCode, maps, odkrMap),
      })),
    [codes, odkrMap, maps]
  );
};
